/**
 * Web Vitals Tracking
 *
 * Tracks Core Web Vitals and sends them to analytics
 * Monitors: LCP, FID, CLS, FCP, TTFB, INP
 *
 * @package pixelkicks Child
 * @since 3.1.0
 */

// Web vitals functions will be loaded dynamically
let onCLS, onFID, onLCP, onFCP, onTTFB, onINP;
let webVitalsLoaded = false;

/**
 * Load web-vitals library dynamically
 */
async function loadWebVitals() {
  try {
    const webVitals = await import("web-vitals");
    onCLS = webVitals.onCLS;
    onFID = webVitals.onFID;
    onLCP = webVitals.onLCP;
    onFCP = webVitals.onFCP;
    onTTFB = webVitals.onTTFB;
    onINP = webVitals.onINP;
    webVitalsLoaded = true;
    return true;
  } catch (error) {
    console.warn(
      "web-vitals library not available. Install with: npm install web-vitals",
    );
    return false;
  }
}

/**
 * Send vitals to analytics endpoint
 * @param {Object} metric - Web vital metric
 */
function sendToAnalytics(metric) {
  const body = JSON.stringify({
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType,
    url: window.location.href,
    userAgent: navigator.userAgent,
  });

  // Send to your analytics endpoint
  // Option 1: Google Analytics 4
  if (typeof window.gtag === "function") {
    window.gtag("event", metric.name, {
      value: Math.round(
        metric.name === "CLS" ? metric.value * 1000 : metric.value,
      ),
      event_category: "Web Vitals",
      event_label: metric.id,
      non_interaction: true,
    });
  }

  // Option 2: Custom endpoint (WordPress AJAX)
  if (typeof ajax_variable !== "undefined") {
    navigator.sendBeacon(
      ajax_variable.ajax_url + "?action=log_web_vitals",
      body,
    );
  }

  // Option 3: Console log for development
  if (globalThis?.process?.env?.NODE_ENV === "development") {
    console.log("Web Vital:", metric);
  }
}

/**
 * Initialize Web Vitals tracking
 */
export async function initWebVitals() {
  // Load web-vitals library first
  const loaded = await loadWebVitals();

  if (!loaded || !webVitalsLoaded) {
    console.info("Web Vitals tracking not initialized (library not available)");
    return;
  }

  try {
    // Track all Core Web Vitals
    onLCP(sendToAnalytics);
    onFID(sendToAnalytics);
    onCLS(sendToAnalytics);
    onFCP(sendToAnalytics);
    onTTFB(sendToAnalytics);

    // Track INP (Interaction to Next Paint) - replaces FID
    if (onINP) {
      onINP(sendToAnalytics);
    }

    // Add performance observer for additional metrics
    if ("PerformanceObserver" in window) {
      // Long tasks monitoring
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              console.warn("Long task detected:", entry);
            }
          }
        });
        longTaskObserver.observe({ entryTypes: ["longtask"] });
      } catch (e) {
        // Long task API not supported
      }

      // Layout shift monitoring
      try {
        const layoutShiftObserver = new PerformanceObserver((list) => {
          let clsScore = 0;
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsScore += entry.value;
            }
          }
          if (clsScore > 0.1) {
            console.warn("High CLS detected:", clsScore);
          }
        });
        layoutShiftObserver.observe({ entryTypes: ["layout-shift"] });
      } catch (e) {
        // Layout shift API not supported
      }
    }

    // Page visibility tracking
    let pageVisibilityTime = 0;
    let visibilityStart = Date.now();

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        pageVisibilityTime += Date.now() - visibilityStart;
      } else {
        visibilityStart = Date.now();
      }
    });

    // Send visibility time on unload
    window.addEventListener("beforeunload", () => {
      if (!document.hidden) {
        pageVisibilityTime += Date.now() - visibilityStart;
      }

      // Send engagement time
      sendToAnalytics({
        name: "EngagementTime",
        value: pageVisibilityTime,
        rating: "good",
        id: "engagement-" + Date.now(),
      });
    });
  } catch (error) {
    console.error("Error initializing Web Vitals:", error);
  }
}

/**
 * Get current performance metrics snapshot
 * @returns {Object} Performance metrics
 */
export function getPerformanceMetrics() {
  if (!window.performance) return null;

  const navigation = performance.getEntriesByType("navigation")[0];
  const paint = performance.getEntriesByType("paint");

  return {
    dns: navigation?.domainLookupEnd - navigation?.domainLookupStart,
    tcp: navigation?.connectEnd - navigation?.connectStart,
    ttfb: navigation?.responseStart - navigation?.requestStart,
    download: navigation?.responseEnd - navigation?.responseStart,
    domInteractive: navigation?.domInteractive,
    domComplete: navigation?.domComplete,
    loadComplete: navigation?.loadEventEnd - navigation?.loadEventStart,
    fcp: paint.find((p) => p.name === "first-contentful-paint")?.startTime,
  };
}
